<template>
  <div class="sidebar bg-white dark:bg-gray-800 bg-opacity-70 backdrop-blur-sm h-screen shadow-lg" :class="{ 'w-64': !collapsed, 'w-16': collapsed }">
    <div class="flex justify-between items-center p-4 border-b">
      <h2 class="text-xl font-bold text-primary-700 dark:text-primary-400" v-if="!collapsed">GeoProperty Hub</h2>
      <button @click="toggleSidebar" class="text-gray-700 hover:text-primary-700 dark:text-gray-300 dark:hover:text-primary-400">
        <i class="fas" :class="collapsed ? 'fa-chevron-right' : 'fa-chevron-left'" aria-hidden="true"></i>
      </button>
    </div>
    
    <div class="py-4">
      <div class="px-4 mb-6">
        <div v-if="authStore.user" class="flex items-center" :class="{'space-x-3': !collapsed}">
          <div class="w-10 h-10 rounded-full bg-primary-600 dark:bg-primary-700 flex items-center justify-center text-white">
            {{ userInitials }}
          </div>
          <div v-if="!collapsed">
            <p class="font-medium dark:text-white">{{ userFullName }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ userRole }}</p>
          </div>
        </div>
      </div>
      
      <nav class="space-y-1">
        <router-link 
          v-for="item in menuItems" 
          :key="item.path"
          :to="item.path"
          class="flex items-center py-3 px-4 hover:bg-primary-50 dark:hover:bg-primary-900 transition-colors text-gray-700 dark:text-gray-200 hover:text-primary-700 dark:hover:text-primary-300"
          active-class="bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-r-4 border-primary-500"
        >
          <i :class="[item.icon, 'text-lg', {'mr-3': !collapsed}]"></i>
          <span v-if="!collapsed">{{ item.name }}</span>
        </router-link>
      </nav>
    </div>
    
    <div class="absolute bottom-0 w-full p-4 border-t">
      <button 
        @click="logout" 
        class="flex items-center text-gray-700 dark:text-gray-200 hover:text-primary-700 dark:hover:text-primary-300 w-full"
      >
        <i class="fas fa-sign-out-alt" :class="{'mr-3': !collapsed}"></i>
        <span v-if="!collapsed">Logout</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';

const authStore = useAuthStore();
const collapsed = ref(false);

const menuItems = [
  { name: 'Dashboard', path: '/dashboard', icon: 'fas fa-home' },
  { name: 'Properties', path: '/properties', icon: 'fas fa-building' },
  { name: 'Data Import', path: '/data-import', icon: 'fas fa-upload' },
  { name: 'Analytics', path: '/analytics', icon: 'fas fa-chart-bar' },
  { name: 'Users', path: '/users', icon: 'fas fa-users' },
  { name: 'Products', path: '/products', icon: 'fas fa-box' },
  { name: 'Settings', path: '/settings', icon: 'fas fa-cog' },
  { name: 'API Docs', path: '/dev-docs', icon: 'fas fa-code' }
];

const toggleSidebar = () => {
  collapsed.value = !collapsed.value;
  // Store the state in localStorage
  localStorage.setItem('sidebar_collapsed', collapsed.value.toString());
};

const logout = () => {
  authStore.logout();
};

// Computed properties for user data
const userInitials = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  
  // Handle case when user data is nested in a 'user' property
  const userData = user.user || user;
  
  const firstName = userData.first_name || '';
  const lastName = userData.last_name || '';
  
  return `${firstName.charAt(0)}${lastName.charAt(0)}`;
});

const userFullName = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  
  // Handle case when user data is nested in a 'user' property
  const userData = user.user || user;
  
  const firstName = userData.first_name || '';
  const lastName = userData.last_name || '';
  
  return `${firstName} ${lastName}`;
});

const userRole = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  
  // Handle case when user data is nested in a 'user' property
  const userData = user.user || user;
  
  return userData.role || '';
});

// Load sidebar state from localStorage on component mount
onMounted(() => {
  const savedState = localStorage.getItem('sidebar_collapsed');
  if (savedState) {
    collapsed.value = savedState === 'true';
  }
});
</script>

<style scoped>
.sidebar {
  position: sticky;
  top: 0;
  left: 0;
  transition: width 0.3s ease;
  flex-shrink: 0;
  z-index: 10;
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
  }
}
</style>